import React, { useEffect, useState } from "react";
import { useAuth } from "../lib/auth";
import api from "../lib/api";
import { toast } from "sonner";
import { User, MapPin, Heart } from "@phosphor-icons/react";

const CULTURES = ["Nigerian", "Ghanaian", "Kenyan", "Zimbabwean", "Southern African", "East African", "Caribbean"];

export default function Profile() {
  const { user, refresh } = useAuth();
  const [prefs, setPrefs] = useState(user?.cultural_preferences || []);
  const [postcode, setPostcode] = useState(user?.default_postcode || "");
  const [newAddr, setNewAddr] = useState({ label: "Home", line1: "", line2: "", city: "", postcode: "", is_default: false });
  const [saving, setSaving] = useState(false);

  const togglePref = (c) => {
    setPrefs((p) => (p.includes(c) ? p.filter((x) => x !== c) : [...p, c]));
  };

  const savePrefs = async () => {
    setSaving(true);
    try {
      await api.put("/auth/me/preferences", { cultural_preferences: prefs, default_postcode: postcode });
      await refresh();
      toast.success("Preferences saved");
    } catch {
      toast.error("Could not save");
    }
    setSaving(false);
  };

  const addAddress = async () => {
    if (!newAddr.line1 || !newAddr.city || !newAddr.postcode) {
      toast.error("Please fill address line 1, city and postcode");
      return;
    }
    try {
      await api.post("/auth/me/addresses", newAddr);
      await refresh();
      setNewAddr({ label: "Home", line1: "", line2: "", city: "", postcode: "", is_default: false });
      toast.success("Address added");
    } catch {
      toast.error("Could not add address");
    }
  };

  if (!user) return null;

  return (
    <div className="pt-24 pb-24 max-w-4xl mx-auto px-4 md:px-8 space-y-6" data-testid="profile-page">
      <div className="card-surface p-6 md:p-8">
        <div className="flex items-center gap-4 mb-4">
          <div className="w-14 h-14 rounded-full bg-[#A94438] text-white flex items-center justify-center font-heading font-black text-xl">
            {(user.name || user.email)[0].toUpperCase()}
          </div>
          <div>
            <h1 className="font-heading text-2xl font-bold tracking-tight">{user.name}</h1>
            <p className="text-sm text-[#5C5C54]">{user.email}</p>
            <p className="text-xs mt-1 chip inline-block capitalize">{user.role}</p>
          </div>
        </div>
      </div>

      <div className="card-surface p-6">
        <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2"><Heart size={20} weight="duotone" /> Cultural preferences</h2>
        <p className="text-xs text-[#5C5C54] mb-4">Used to personalise your homepage recommendations.</p>
        <div className="flex flex-wrap gap-2 mb-4">
          {CULTURES.map((c) => (
            <button
              key={c}
              onClick={() => togglePref(c)}
              data-testid={`pref-${c.toLowerCase().replace(/\s/g, "-")}`}
              className={`rounded-full px-4 py-2 text-xs font-bold transition-all ${prefs.includes(c) ? "bg-[#A94438] text-white" : "bg-white border border-[#E5E2D9] text-[#5C5C54] hover:border-[#D1CEC5]"}`}
            >
              {c}
            </button>
          ))}
        </div>
        <div className="mb-4">
          <label className="block text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-1.5">Default postcode</label>
          <input data-testid="default-postcode-input" value={postcode} onChange={(e) => setPostcode(e.target.value.toUpperCase())} className="input-field uppercase max-w-xs" />
        </div>
        <button data-testid="save-prefs-btn" onClick={savePrefs} disabled={saving} className="btn-primary text-sm disabled:opacity-50">
          {saving ? "Saving..." : "Save preferences"}
        </button>
      </div>

      <div className="card-surface p-6">
        <h2 className="font-heading text-xl font-bold mb-4 flex items-center gap-2"><MapPin size={20} weight="duotone" /> Saved addresses</h2>
        {user.addresses?.length > 0 ? (
          <div className="space-y-2 mb-5">
            {user.addresses.map((a) => (
              <div key={a.address_id} className="p-3 rounded-xl border border-[#E5E2D9] flex items-center justify-between" data-testid="saved-address">
                <div>
                  <p className="font-bold text-sm">{a.label} {a.is_default && <span className="chip ml-1">Default</span>}</p>
                  <p className="text-xs text-[#5C5C54]">{a.line1}{a.line2 ? `, ${a.line2}` : ""}, {a.city}, {a.postcode}</p>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <p className="text-sm text-[#5C5C54] mb-4">No addresses yet.</p>
        )}

        <div className="border-t border-[#E5E2D9] pt-5">
          <p className="text-xs font-bold tracking-wider uppercase text-[#5C5C54] mb-3">Add new address</p>
          <div className="grid md:grid-cols-2 gap-3">
            <input data-testid="addr-label" placeholder="Label (Home, Work)" value={newAddr.label} onChange={(e) => setNewAddr({ ...newAddr, label: e.target.value })} className="input-field" />
            <input data-testid="addr-line1" placeholder="Address line 1" value={newAddr.line1} onChange={(e) => setNewAddr({ ...newAddr, line1: e.target.value })} className="input-field md:col-span-2" />
            <input data-testid="addr-line2" placeholder="Address line 2 (optional)" value={newAddr.line2} onChange={(e) => setNewAddr({ ...newAddr, line2: e.target.value })} className="input-field md:col-span-2" />
            <input data-testid="addr-city" placeholder="City" value={newAddr.city} onChange={(e) => setNewAddr({ ...newAddr, city: e.target.value })} className="input-field" />
            <input data-testid="addr-postcode" placeholder="Postcode" value={newAddr.postcode} onChange={(e) => setNewAddr({ ...newAddr, postcode: e.target.value.toUpperCase() })} className="input-field uppercase" />
          </div>
          <label className="flex items-center gap-2 text-xs text-[#5C5C54] mt-3">
            <input type="checkbox" checked={newAddr.is_default} onChange={(e) => setNewAddr({ ...newAddr, is_default: e.target.checked })} /> Set as default
          </label>
          <button data-testid="add-address-btn" onClick={addAddress} className="btn-primary text-sm mt-4">Add address</button>
        </div>
      </div>
    </div>
  );
}
